import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Header } from '../../components/Header';
import { Pagination } from '../../components/Pagination';
import { TableHead } from './OptimizedTableHead';
import { TableBody } from './OptimizedTableBody';
import type {
  ColumnOrderState,
  SortingState,
  FilterFn,
  ColumnPinningState,
  Row,
} from '@tanstack/react-table';
import {
  EditableOnlyProvider,
  TableCoreProvider,
  type TableInstanceContextValue,
} from './OptimizedTableProvider';
import { TableProvider } from '../../providers/TableProvider';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import {
  RowSelection,
  TableProps,
  TableVariant,
  type TableRowBackgroundContext,
} from '../../types';
import {
  Container,
  EmptyStateInnerWrap,
  EmptyStateScrollRegion,
  OuterContainer,
  OverLay,
  TableStyles,
  TableWrapper,
} from './OptimizedTable.styles';
import {
  createSelectorColumn,
  getTableMeta,
  getTotalHeadersSize,
  updateSorting,
} from '../../utils';
import { OptimizedDefaultCell } from './OptimizedDefaultCell';
import { rankItem } from '@tanstack/match-sorter-utils';
import { Plus } from '@phosphor-icons/react';
import { omit, omitBy, size } from 'lodash';
import useLocalStorageState from 'use-local-storage-state';
import { useTableEvents } from '../../hooks';
import { useGenerateVirtualRows, useInitialHiddenColumns, useVisibleColumnRange } from '../hooks';
import { clearTableStateStorage } from '../utils/clearTableStateOnFullPageLoad';
import { EmptyState } from 'components';
import { TableLoader } from '../../components/TableLoader';
import type { View } from 'types/views';
import { deserializeTableSettings } from '../../components/views/tableSettings';

const minCellWidth = 96;
const maxCellWidth = 500;
const perPage = 20;
const oldRowHeights = { compact: 44, default: 64, comfortable: 80 };
const rowHeights = { compact: 32, default: 40, comfortable: 48 };
const DEFAULT_OVERSCAN = 20;
const SCROLL_OVERSCAN = 100;
const TABLE_VARIANT_DEFAULT: TableVariant = 'default';

let tableStateClearedThisPageLoad = false;

export interface TypeId {
  id?: string | number;
}

type TableInnerProps<T> = Omit<TableProps<T>, 'editableState' | 'setEditableState'>;

const noopSetEditableState = (): void => undefined;

/** Inner table: all state and useReactTable. Memoized so it does not re-run when only editableState (prop) changes. */
const OptimizedTableInnerComponent = <T extends TypeId>({
  rowSelection: rowSelectionProps,
  setRowSelection: setRowSelectionProps,
  getRowId,
  isShowHeaderEmptyData = false,
  initialHidden = [],
  initialVisible = [],
  headerAddButtonProps,
  getRowCanExpand,
  renderExpandedComponent,
  expandedRowHeight,
  useFloatingBulkBar,
  onSelectionChange,
  showInlinePagination = true,
  variant = TABLE_VARIANT_DEFAULT,
  expandedRowBackgroundColor,
  getRowBackground,
  ...props
}: TableInnerProps<T>) => {
  let rowHeight = props.rowHeight ? rowHeights[props.rowHeight] : rowHeights.default;
  if (props.config?.v1) {
    rowHeight = props.rowHeight ? oldRowHeights[props.rowHeight] : oldRowHeights.default;
  }
  const initialPin = props?.config?.row?.selectable
    ? { left: ['Selector', ...(props.initialPinned || [])], right: [] }
    : props.initialPinned
      ? { left: [...(props.initialPinned || [])], right: [] }
      : { left: [], right: [] };

  const columnIds = props.columns?.map(col => col.id);

  const defaultColumnOrder = useMemo(
    () =>
      props?.config?.row?.selectable && columnIds?.length
        ? (['Selector', ...columnIds] as ColumnOrderState)
        : [],
    [props?.config?.row?.selectable, columnIds]
  );

  const initialColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    for (const key of initialHidden) {
      if (key) visibility[key] = false;
    }
    if (initialVisible?.length > 0) {
      for (const id of columnIds) {
        if (id && !initialVisible?.includes(id)) {
          visibility[id] = false;
        }
      }
    }
    return visibility;
  }, [initialHidden, initialVisible, columnIds]);

  const [expanded, setExpanded] = useState({});
  const [sorting, setSorting] = useLocalStorageState<SortingState>(`${props.name}-tableSorting`, {
    defaultValue: [],
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorageState(
    `${props.name}-tableColumnVisibility`,
    { defaultValue: initialColumnVisibility }
  );

  useInitialHiddenColumns({ name: props.name, initialHidden, setColumnVisibility });

  const [columnOrder, setColumnOrder] = useLocalStorageState<ColumnOrderState>(
    `${props.name}-tableColumnOrder`,
    { defaultValue: defaultColumnOrder }
  );

  // Ensure Selector is ALWAYS first when table is selectable - this is non-negotiable
  const effectiveColumnOrder = useMemo((): ColumnOrderState => {
    const isSelectable = props?.config?.row?.selectable;

    if (!isSelectable) {
      // Not selectable - use columnOrder as-is (no Selector column exists)
      return columnOrder;
    }

    // Table is selectable - Selector MUST be first
    if (columnOrder.length === 0) {
      // No saved order - create default with Selector first
      const validColumnIds = columnIds?.filter((id): id is string => id !== undefined) ?? [];
      return validColumnIds.length ? ['Selector', ...validColumnIds] : ['Selector'];
    }

    if (columnOrder[0] === 'Selector') {
      // Already correct
      return columnOrder;
    }

    // Selector exists but not first - move it to front
    return ['Selector', ...columnOrder.filter(id => id !== 'Selector')];
  }, [props?.config?.row?.selectable, columnOrder, columnIds]);

  const [columnPinning, setColumnPinning] = useLocalStorageState<ColumnPinningState>(
    `${props.name}-tableColumnPinned`,
    { defaultValue: initialPin }
  );

  // Ensure Selector is ALWAYS first in pinned columns when table is selectable
  const effectiveColumnPinning = useMemo((): ColumnPinningState => {
    const isSelectable = props?.config?.row?.selectable;

    if (!isSelectable) {
      // Not selectable - use columnPinning as-is
      return columnPinning;
    }

    const left = columnPinning.left ?? [];

    // If Selector is already first, return as-is
    if (left[0] === 'Selector') {
      return columnPinning;
    }

    // Move Selector to front of left pinned columns
    return {
      ...columnPinning,
      left: ['Selector', ...left.filter(id => id !== 'Selector')],
    };
  }, [props?.config?.row?.selectable, columnPinning]);

  const [columnSizing, setColumnSizing] = useLocalStorageState(`${props.name}-tableColumnSizing`, {
    defaultValue: {},
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelectionInside, setRowSelectionInside] = useState<RowSelection>({});

  useLayoutEffect(() => {
    if (tableStateClearedThisPageLoad) return;
    if (!columnIds?.length) return;

    tableStateClearedThisPageLoad = true;
    clearTableStateStorage();

    const visibility: Record<string, boolean> = {};
    for (const id of columnIds) {
      if (id) {
        visibility[id] = !initialHidden.includes(id);
      }
    }
    if (initialVisible?.length > 0) {
      for (const id of columnIds) {
        if (id && !initialVisible.includes(id)) {
          visibility[id] = false;
        }
      }
    }
    setColumnVisibility(visibility);
    setColumnPinning(initialPin);
    setColumnOrder(
      props?.config?.row?.selectable && columnIds?.length
        ? (['Selector', ...columnIds] as ColumnOrderState)
        : []
    );
    setColumnSizing({});
    setSorting([]);
  }, [
    initialHidden,
    initialVisible,
    columnIds,
    initialPin,
    setColumnVisibility,
    setColumnPinning,
    setColumnOrder,
    setColumnSizing,
    setSorting,
  ]);

  const isControlledRowSelection =
    rowSelectionProps !== undefined && typeof setRowSelectionProps === 'function';

  const rowSelection = useMemo(() => {
    return isControlledRowSelection ? rowSelectionProps : rowSelectionInside;
  }, [isControlledRowSelection, rowSelectionProps, rowSelectionInside]);

  const setRowSelection = useCallback(
    (updater: React.SetStateAction<RowSelection>) => {
      if (isControlledRowSelection) {
        setRowSelectionProps(updater);
      } else {
        setRowSelectionInside(updater);
      }
    },
    [isControlledRowSelection, setRowSelectionProps, setRowSelectionInside]
  );

  const columnResizeMode = 'onChange';
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const selectorColumn = useMemo(
    () =>
      createSelectorColumn<T>(
        props?.config?.row?.selectable,
        props?.config?.isExpandable,
        props?.config?.subRowsDepth,
        props.config?.v1
      ),
    [
      props?.config?.row?.selectable,
      props?.config?.isExpandable,
      props?.config?.subRowsDepth,
      props.config?.v1,
    ]
  );

  const defaultColumn = useMemo(
    () => ({
      minSize: minCellWidth,
      maxSize: maxCellWidth,
      cell: OptimizedDefaultCell<T>,
    }),
    []
  );

  const cols = useMemo(
    () => [...selectorColumn, ...props.columns],
    [selectorColumn, props.columns]
  );

  const fuzzyFilter: FilterFn<T> = useCallback((row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta(itemRank);
    return itemRank.passed;
  }, []);

  const enableRowSelection = useCallback((row: Row<T>) => row?.original.id !== -1, []);

  const getSubRowsKey = props?.config?.getSubRowsKey;
  const getSubRows = useCallback(
    (row: T) =>
      (row as T & { subRows: T[] })?.subRows ||
      (row as T & Record<string, unknown>)?.[getSubRowsKey ?? ''] ||
      [],
    [getSubRowsKey]
  );

  useTableEvents(
    columnVisibility,
    effectiveColumnOrder,
    effectiveColumnPinning,
    columnSizing,
    rowSelection,
    props.config?.event
  );

  // Store refs for values needed in callbacks to avoid recreating them
  const propsRef = useRef(props);
  propsRef.current = props;

  const onSortingChange = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      if (typeof updater !== 'function') return;
      const currentProps = propsRef.current;
      const sortArray = updater(table.getState().sorting);
      const sortState = updateSorting(sortArray, currentProps.meta?.tableState);
      if (currentProps.config?.event) {
        currentProps.config.event('sorting', {
          sort: sortState.sort || '',
          order: sortState.order || '',
        });
      }
      if (table.options.meta?.onTableStateChange && Boolean(currentProps.onPaginationChange)) {
        table.options.meta?.onTableStateChange({
          sort: sortState.sort,
          order: sortState.order,
          page: null,
          cursor: null,
        });
        return;
      }
      setSorting(sortArray);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSorting]
  );

  const onPaginationChangeHandler = useCallback(
    (updater: React.SetStateAction<{ pageIndex: number; pageSize: number }>) => {
      if (typeof updater !== 'function') return;
      const newPageInfo = updater(table.getState().pagination);
      propsRef.current.onPaginationChange?.(newPageInfo);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const selectedRowKeys = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([rowId]) => rowId),
    [rowSelection]
  );

  // Use a ref-based setData for getTableMeta so we don't need a local data mirror
  const dataRef = useRef(props.data);
  dataRef.current = props.data;
  const setDataForMeta = useCallback((updater: React.SetStateAction<T[]>) => {
    // getTableMeta uses setData for updateData; the actual data comes from props.data
    // so this is a no-op sink – mutations go through the meta callbacks instead
    void updater;
  }, []);

  const tableMeta = useMemo(
    () => ({
      ...getTableMeta(setDataForMeta, props.meta, selectedRowKeys),
    }),
    [setDataForMeta, props.meta, selectedRowKeys]
  );

  const table = useReactTable({
    getRowId,
    data: props.data,
    defaultColumn: props.defaultColumn ? props.defaultColumn : defaultColumn,
    columns: cols,
    getRowCanExpand,
    ...(props.onPaginationChange
      ? {
          manualPagination: true,
          pageCount: props.pageCount || -1,
          onPaginationChange: onPaginationChangeHandler,
        }
      : {
          initialState: {
            pagination: {
              pageIndex: props.config?.pagination?.pageIndex
                ? props.config.pagination.pageIndex
                : 0,
              pageSize: props.config?.pagination ? props.config.pagination.pageSize : perPage,
            },
          },
        }),
    state: {
      expanded: {
        ...expanded,
        paginateExpandedRows: Boolean(props.onPaginationChange),
      },
      columnOrder: effectiveColumnOrder,
      sorting,
      columnPinning: effectiveColumnPinning,
      columnSizing,
      columnVisibility,
      globalFilter: props.meta?.tableState ? undefined : globalFilter,
      rowSelection,
      ...(props.onPaginationChange
        ? {
            pagination: props.config?.pagination,
            sorting: props.meta?.tableState?.sort
              ? [{ id: props.meta.tableState.sort, desc: props.meta.tableState.order === 'DESC' }]
              : undefined,
          }
        : {}),
    },
    columnResizeMode: 'onChange',
    enableRowSelection,
    globalFilterFn: props?.fuzzyFilter ? props.fuzzyFilter : fuzzyFilter,
    paginateExpandedRows: Boolean(props.onPaginationChange),
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange,
    manualSorting: Boolean(props.onPaginationChange),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onExpandedChange: setExpanded,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    meta: tableMeta,
    debugTable: false,
  });

  // Table width
  const tableWidth = getTotalHeadersSize(table);

  // Get the row model for virtualization
  const rows = table.getRowModel().rows;

  // Create virtual items that include expanded rows
  const virtualRows = useMemo(() => {
    if (!renderExpandedComponent) {
      return rows;
    }
    const virtualItems: (
      | (typeof rows)[0]
      | { isExpandedRow: true; parentRow: (typeof rows)[0] }
    )[] = [];

    rows.forEach(row => {
      virtualItems.push(row);
      if (row.getIsExpanded()) {
        virtualItems.push({ isExpandedRow: true, parentRow: row });
      }
    });

    return virtualItems;
  }, [rows, renderExpandedComponent]);

  // Memoize the renderExpandedComponent to prevent unnecessary re-renders
  const memoizedRenderExpandedComponent = useCallback(
    (row?: Row<T>) => {
      return renderExpandedComponent?.(row);
    },
    [renderExpandedComponent]
  );

  // Tracks scroll state (for TableTooltip + dynamic overscan) and which columns
  // intersect the viewport (for horizontal virtualization).
  const { isScrolling, visibleColumnRange } = useVisibleColumnRange(table, tableContainerRef);

  const { virtualItems, totalSize } = useGenerateVirtualRows<T>(virtualRows, tableContainerRef, {
    disabled: props.config?.disableVirtual,
    rowHeight,
    expandedRowHeight,
    overscan: isScrolling ? SCROLL_OVERSCAN : DEFAULT_OVERSCAN,
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const remainingWidth = useMemo(() => {
    const width = scrollContainerRef.current?.getBoundingClientRect().width;
    if (!width) return 0;
    const diff = width - table.getTotalSize();
    return Math.max(diff, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef.current, table]);

  useEffect(() => {
    if (onSelectionChange) {
      const count = Object.keys(rowSelection).length;
      onSelectionChange(
        count,
        () => table.resetRowSelection(),
        () => table.getSelectedRowModel()?.flatRows.map(r => r.original) ?? []
      );
    }
  }, [rowSelection, onSelectionChange, table]);

  // ── Split context values (memoized individually) ──

  const stableValue = useMemo(
    () => ({
      config: props.config
        ? { ...props.config, tableId: props.name }
        : ({} as typeof props.config & { tableId?: string }),
      setGlobalFilter,
      onTableStateChange: props.meta?.onTableStateChange,
      variant,
      expandedRowBackgroundColor,
      getRowBackground: getRowBackground as
        | ((ctx: TableRowBackgroundContext<unknown>) => string | undefined)
        | undefined,
    }),
    [
      props.config,
      props.name,
      setGlobalFilter,
      props.meta?.onTableStateChange,
      variant,
      expandedRowBackgroundColor,
      getRowBackground,
    ]
  );

  const tableInstanceValue = useMemo(
    () => ({ table, globalFilter, rowSelection }),
    [table, globalFilter, rowSelection]
  );

  const scrollValue = useMemo(() => ({ isScrolling }), [isScrolling]);

  // Legacy context value for Header, Pagination, Filter (no editable – editable comes from EditableOnlyProvider above)
  const legacyContextValue = useMemo(
    () => ({
      config: stableValue.config,
      setGlobalFilter: stableValue.setGlobalFilter,
      onTableStateChange: stableValue.onTableStateChange,
      table: tableInstanceValue.table,
      globalFilter: tableInstanceValue.globalFilter,
      isScrolling: scrollValue.isScrolling,
      variant,
      expandedRowBackgroundColor,
      getRowBackground: getRowBackground as
        | ((ctx: TableRowBackgroundContext<unknown>) => string | undefined)
        | undefined,
    }),
    [
      stableValue,
      tableInstanceValue,
      scrollValue,
      variant,
      expandedRowBackgroundColor,
      getRowBackground,
    ]
  );

  // ── Empty state (memoized) ──

  const emptyStateNode = useMemo(() => {
    if (props.data.length > 0 || props.isLoading) return null;
    const hasActiveFilters =
      size(
        omitBy(
          omit({ ...props.meta?.tableState }, ['order', 'sort']),
          props?.excludedKeysEmptyCheck
        )
      ) > 0;
    const isNoResults = hasActiveFilters;
    const isNoData = !hasActiveFilters;

    return (
      <OverLay abs $headerHeight={rowHeight}>
        <EmptyStateScrollRegion>
          <EmptyStateInnerWrap>
            <EmptyState
              type={isNoResults ? 'noResults' : 'noData'}
              img={isNoResults ? props.config?.texts?.noResultsImg : props.config?.texts?.noDataImg}
              title={
                isNoResults ? props.config?.texts?.noResultsTitle : props.config?.texts?.noDataTitle
              }
              description={
                isNoResults
                  ? props.config?.texts?.noResultsDescription
                  : props.config?.texts?.noDataDescription
              }
              showAction={isNoData && Boolean(props?.config?.texts?.addButtonName)}
              addRow={isNoData ? table.options?.meta?.addRow || '' : undefined}
              actionProps={
                isNoData
                  ? {
                      startIcon: <Plus size={16} color="white" />,
                      children: props?.config?.texts?.addButtonName,
                      variant: 'primary',
                      size: 'md',
                    }
                  : undefined
              }
            />
          </EmptyStateInnerWrap>
        </EmptyStateScrollRegion>
      </OverLay>
    );
  }, [
    props.data.length,
    props.isLoading,
    props.meta?.tableState,
    props?.excludedKeysEmptyCheck,
    props.config?.texts,
    table.options?.meta?.addRow,
    rowHeight,
  ]);

  const onPageChange = useCallback(() => {
    if (tableContainerRef?.current) {
      tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // When a saved view is applied via the ViewDropdown, decode its column layout
  // (`_ts`) and apply it through the React state setters. Calling
  // `useLocalStorageState`'s setter (rather than writing localStorage directly)
  // is what guarantees the table re-renders with the new visibility/pinning/
  // order/sizing — direct localStorage writes from another component do not
  // notify in-tab consumers of the same key.
  const parentOnViewChange = props.onViewChange;
  const handleViewChange = useCallback(
    (view: View | null) => {
      if (view) {
        const tsString =
          typeof view.filterCriteria?._ts === 'string' ? view.filterCriteria._ts : '';
        if (tsString) {
          const next = deserializeTableSettings(tsString);
          setColumnVisibility(next.columnVisibility);
          setColumnOrder(next.columnOrder);
          setColumnPinning(next.columnPinning);
          setColumnSizing(next.columnSizing);
        }
      } else {
        // "Show all items" — make every column visible (including
        // default-hidden ones such as custom fields). Writing an explicit
        // `true` for each id prevents `useInitialHiddenColumns` from re-hiding
        // them after async custom-field definitions resolve.
        const allVisible: Record<string, boolean> = {};
        for (const id of columnIds ?? []) {
          if (id) allVisible[id] = true;
        }
        setColumnVisibility(allVisible);
        setColumnOrder(defaultColumnOrder);
        setColumnPinning(initialPin);
        setColumnSizing({});
      }
      parentOnViewChange?.(view);
    },
    [
      setColumnVisibility,
      setColumnOrder,
      setColumnPinning,
      setColumnSizing,
      columnIds,
      defaultColumnOrder,
      initialPin,
      parentOnViewChange,
    ]
  );

  return (
    <TableProvider value={legacyContextValue}>
      <TableCoreProvider
        stable={stableValue}
        tableInstance={tableInstanceValue as TableInstanceContextValue<unknown>}
        scroll={scrollValue}
      >
        <TableWrapper
          data-testid={
            (props as TableProps<T> & { 'data-testid'?: string })['data-testid'] ||
            `table-${props.name}`
          }
        >
          {!props.config?.hideDefaultControlsHeader && (
            <Header
              addButtonProps={headerAddButtonProps}
              filterTitle={props?.filterTitle}
              rowSelectionCount={props.rowSelectionCount ?? Object.keys(rowSelection).length}
              addButtonName={
                props.data.length === 0 ? undefined : props.config?.texts?.addButtonName
              }
              tableId={props?.name}
              filters={props.filters}
              viewConfig={props.viewConfig}
              onViewChange={handleViewChange}
              useFloatingBulkBar={useFloatingBulkBar ?? !props.config?.row?.selectable}
            />
          )}
          <OuterContainer id="megayaee" ref={scrollContainerRef} $variant={variant}>
            {emptyStateNode}
            <Container
              ref={tableContainerRef}
              id={props.name}
              data-testid={`table_${props.name}_container`}
            >
              <TableStyles
                style={{ minWidth: tableWidth }}
                rowHeight={rowHeight}
                fillerPosition={table.getTotalSize()}
                remainingWidth={remainingWidth}
                data-testid={`table_${props.name}_table`}
              >
                <TableHead
                  columnResizeMode={columnResizeMode}
                  isExpandable={props?.config?.isExpandable}
                  rowHeight={rowHeight}
                  v1={props?.config?.v1}
                />
                {props.isLoading ? (
                  <TableLoader
                    top={rowHeight}
                    columns={table.getVisibleLeafColumns().length}
                    variant={variant}
                  />
                ) : null}
                {props.data.length > 0 && (
                  <TableBody
                    virtualItems={virtualItems}
                    totalSize={totalSize}
                    rowHeight={rowHeight}
                    visibleColumnRange={visibleColumnRange}
                    {...(renderExpandedComponent
                      ? { renderExpandedComponent: memoizedRenderExpandedComponent, virtualRows }
                      : {})}
                  />
                )}
              </TableStyles>

              {props?.config?.pagination &&
              !props.isLoading &&
              props.data.length > 0 &&
              (showInlinePagination ?? true) ? (
                <Pagination onPageChange={onPageChange} />
              ) : null}
            </Container>
          </OuterContainer>
        </TableWrapper>
      </TableCoreProvider>
    </TableProvider>
  );
};

const OptimizedTableInner = memo(OptimizedTableInnerComponent) as <T extends TypeId>(
  props: TableInnerProps<T>
) => JSX.Element;

/** Outer table: only provides editable context so inner (memo) does not re-run when only editableState changes. */
export const Table = <T extends TypeId>({
  editableState,
  setEditableState,
  ...propsWithoutEditable
}: TableProps<T>) => {
  const editableValue = useMemo(
    () => ({
      editableState: editableState ?? null,
      setEditableState: setEditableState ?? noopSetEditableState,
    }),
    [editableState, setEditableState]
  );
  return (
    <EditableOnlyProvider value={editableValue}>
      <OptimizedTableInner<T> {...propsWithoutEditable} />
    </EditableOnlyProvider>
  );
};

// Styled components live in ./OptimizedTable.styles
export { Container, OuterContainer, TableStyles } from './OptimizedTable.styles';

export default Table;
